import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { Box, Tooltip, styled } from '@mui/material';
import { Trash } from '@styled-icons/bootstrap/Trash';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import { OTHER_CERTIFICATE_ID } from 'FeatureModules/Document/utils/document.utils';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { BASE_PARTNER_COMPANY_FRAGMENT } from 'graphql/secure/fragments';
import { DOCUMENT_FRAGMENT } from 'graphql/secure/fragments/document.fragments';
import { BASE_SITE_FRAGMENT } from 'graphql/secure/fragments/site.fragments';
import { UPDATE_DOCUMENT } from 'graphql/secure/mutations';
import { TCreateDocumentInput } from 'graphql/secure/mutations/types/document-mutation.types';
import { GET_ALL_PARTNER_COMPANIES } from 'graphql/secure/queries/partner.queries';
import React, { FC } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import {
  EDocumentCategory,
  EDocumentVisibilityStatus,
  IDocument,
} from 'FeatureModules/Document/types/document.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { IBaseSite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import EditDocumentDialogForm from '../Forms/EditDocumentDialogForm';

interface IEditDocumentContainerProps {
  documentId: string;
  hideCancelButton?: boolean;
  renderedOnDialog?: boolean;
  defaultSiteId?: string;
  defaultOwnedById?: string;
  isRequestDocument?: boolean;
  onDelete: () => void;
  onUpdate?: () => void;
  onClose?: () => void;
}

const Container = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: '2px solid',
  borderColor: theme.palette.grey[200],
}));

const Footer = styled('div')<{
  renderedOnDialog?: Tbooleanish;
}>(({ theme, renderedOnDialog }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  bottom: 0,
  left: 0,
  position: renderedOnDialog === 'true' ? 'absolute' : 'static',
  padding: renderedOnDialog === 'true' ? theme.spacing(3, 4) : theme.spacing(2, 0, 0),
}));

const EditDocumentContainer: FC<IEditDocumentContainerProps> = ({
  documentId,
  hideCancelButton,
  defaultOwnedById,
  defaultSiteId,
  renderedOnDialog,
  isRequestDocument = false,
  onClose,
  onUpdate,
  onDelete,
}) => {
  const { openDialog } = useDialog();
  const client = useApolloClient();

  const editedDocument = documentId
    ? client.readFragment<IDocument>({
        fragment: DOCUMENT_FRAGMENT,
        id: `Document:${documentId}`,
        fragmentName: 'documentValues',
      })
    : null;

  const defaultOwnedBy = defaultOwnedById
    ? client.readFragment({
        fragment: BASE_PARTNER_COMPANY_FRAGMENT,
        id: `Company:${defaultOwnedById}`,
        fragmentName: 'basePartnerCompanyValues',
      })
    : null;

  const defaultSite = defaultSiteId
    ? client.readFragment<IBaseSite>({
        fragment: BASE_SITE_FRAGMENT,
        id: `Site:${defaultSiteId}-en`,
        fragmentName: 'baseSiteValues',
      })
    : null;

  // Documents that belong to the category certificate and have the certificate type other do not have a certificateCatalogItem in the backend.
  // So that the certificate autocomplete is not empty, a certificate id is assigned here.
  const certificateCatalogItemId =
    !editedDocument?.certificateCatalogItem &&
    renderedOnDialog &&
    editedDocument?.category === EDocumentCategory.CERTIFICATE
      ? OTHER_CERTIFICATE_ID
      : editedDocument?.certificateCatalogItem?.id;

  const initialDocument = {
    ...(editedDocument ? editedDocument : {}),
    linkedSites:
      (defaultSite ? [defaultSite] : undefined) ||
      (editedDocument?.linkedSites
        ? removeGraphConnections(editedDocument?.linkedSites)
        : undefined),
    ownedBy: defaultOwnedBy || editedDocument?.ownedBy,
    certificateCatalogItemId,
  };

  const { setErrorMessage, setSuccessMessage } = useMessages();

  const [updateDocument] = useMutation(UPDATE_DOCUMENT, {
    onCompleted: ({ updateDocument: { document } }) => {
      // open the request certification dialog if document is certification but has no id
      if (document.category === 'CERTIFICATE' && !document.certificateCatalogItem?.id) {
        openDialog({
          type: 'REQUEST_CERTIFICATION',
          props: { documentTitle: editedDocument?.title },
        });
      }
      setSuccessMessage('Document updated successfully');
      onUpdate?.();
      onClose?.();
    },
    onError: error => {
      if (error.message === 'DOCUMENT_UPDATE_FORBIDDEN_IF_USED') {
        setErrorMessage('You cannot update this document because it is already used in a request.');
      } else {
        setErrorMessage('There was an error updating your Document');
      }
    },
    refetchQueries: ['GetSharedDocuments', 'GetDocuments'],
  });
  const [getConnectedCompanies] = useLazyQuery<{
    partners: IGraphQlConnection<IBasePartnerCompany>;
  }>(GET_ALL_PARTNER_COMPANIES);

  const handleSave = async (values: TCreateDocumentInput) => {
    const selectedCategory = Object.keys(EDocumentCategory).find(key => key === values.category);

    // later on we will offer the possibility to select individual companies with which to share the documents.
    // For now all connected companies will be automatically selected
    let connectedCompanyIds: string[] = [];
    if (values.visibility === EDocumentVisibilityStatus.SELECTED) {
      const { data: { partners: { edges = [] } = {} } = {} } = await getConnectedCompanies({
        variables: { filters: { partnerRole: 'SUPPLIER' } },
      });
      connectedCompanyIds = edges.map(({ node }) => node.id);
    }

    const linkedSiteIds = values.linkedSites?.map(site => site.id) || [];

    // Sanitize filename to prevent directory traversal
    const fileName = values.title.replace(/[^a-z0-9.]/gi, '_').toLowerCase();

    return await updateDocument({
      variables: {
        id: documentId,
        input: {
          fileName,
          title: values.title,
          expiryDate: values.expiryDate,
          issuedDate: values.issuedDate,
          category: selectedCategory,
          ownedById: values.ownedBy?.id || null,
          linkedSiteIds,
          comment: values.comment,
          visibility: values.visibility,
          // to distinguish between other certificate and no certificate, we use the the OTHER_CERTIFICATE_ID in the frontend.
          // In the backend, both are null, so we have to filter the id out here.
          certificateCatalogItemId:
            values.certificateCatalogItemId === OTHER_CERTIFICATE_ID
              ? null
              : values.certificateCatalogItemId,
          sharedWithCompanyIds: connectedCompanyIds,
        },
      },
    });
  };

  return (
    <Container>
      <EditDocumentDialogForm
        initialDocument={initialDocument}
        disableLinkedSites={!!defaultSite}
        disableOwnedBy={!!defaultOwnedBy}
        onSubmit={handleSave}
        isRequestDocument={isRequestDocument}
      >
        {({ submitForm, isSubmitting }) => (
          <Footer renderedOnDialog={Booleanish(renderedOnDialog)}>
            <ThemeButton
              color="BLUE_ICE"
              size="large"
              startIcon={<Trash size={14} />}
              data-testid="delete-document-btn"
              onClick={onDelete}
            >
              Delete
            </ThemeButton>
            <Box display="flex">
              {onClose && !hideCancelButton && (
                <Box mr={1}>
                  <ThemeButton color="BLUE_ICE" size="large" onClick={onClose}>
                    Cancel
                  </ThemeButton>
                </Box>
              )}
              <Tooltip title="Make sure that all required fields are set">
                <div>
                  <ThemeButton
                    onClick={submitForm}
                    loading={isSubmitting}
                    size="large"
                    color="PRIMARY"
                    data-testid="save-edited-document-btn"
                  >
                    Apply changes
                  </ThemeButton>
                </div>
              </Tooltip>
            </Box>
          </Footer>
        )}
      </EditDocumentDialogForm>
    </Container>
  );
};

export default EditDocumentContainer;
